import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { Settings as SettingsIcon, Webhook as WebhookIcon } from '@mui/icons-material';

export default function ChangeDetectionConfigPage() {
  const [webhookUrl, setWebhookUrl] = useState('https://example.com/webhook');
  const [webhookEnabled, setWebhookEnabled] = useState(true);
  const [metaChangeEnabled, setMetaChangeEnabled] = useState(true);
  const [canonicalChangeEnabled, setCanonicalChangeEnabled] = useState(true);
  const [indexabilityChangeEnabled, setIndexabilityChangeEnabled] = useState(true);
  const [contentChangeEnabled, setContentChangeEnabled] = useState(true);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // TODO: Implement actual save functionality
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <SettingsIcon fontSize="large" color="primary" />
        <Box>
          <Typography variant="h5">Change Detection Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure change detection settings, webhooks, and notification preferences
          </Typography>
        </Box>
      </Stack>

      {saved && (
        <Alert severity="success">Configuration saved successfully!</Alert>
      )}

      {/* Webhook Configuration */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <WebhookIcon color="action" />
            <Typography variant="subtitle1" fontWeight="bold">
              Webhook Configuration
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={webhookEnabled}
                  onChange={(e) => setWebhookEnabled(e.target.checked)}
                />
              }
              label="Enable Webhook Notifications"
            />
            
            <TextField
              label="Webhook URL"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              fullWidth
              disabled={!webhookEnabled}
              placeholder="https://your-domain.com/webhook/changes"
              helperText="Changes will be sent to this URL in real-time"
            />

            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Webhook Payload Example
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                }}
              >
                <pre>{`{
  "tenant_id": 1,
  "page_id": "https://example.com/page",
  "change_type": "META_CHANGE",
  "detected_at": "2026-01-12T10:30:00Z",
  "change_hash": "abc123..."
}`}</pre>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Change Type Filters */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Monitored Change Types
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select which types of changes to monitor and report
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={metaChangeEnabled}
                  onChange={(e) => setMetaChangeEnabled(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="Meta Tags" color="info" size="small" />
                  <Typography variant="body2">Title, description, and meta tag changes</Typography>
                </Stack>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={canonicalChangeEnabled}
                  onChange={(e) => setCanonicalChangeEnabled(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="Canonical" color="warning" size="small" />
                  <Typography variant="body2">Canonical URL changes</Typography>
                </Stack>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={indexabilityChangeEnabled}
                  onChange={(e) => setIndexabilityChangeEnabled(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="Indexability" color="error" size="small" />
                  <Typography variant="body2">Robots meta, noindex changes</Typography>
                </Stack>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={contentChangeEnabled}
                  onChange={(e) => setContentChangeEnabled(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label="Content" color="success" size="small" />
                  <Typography variant="body2">Page content and structure changes</Typography>
                </Stack>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Detection Settings */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Detection Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <TextField
              label="Detection Frequency"
              select
              value="daily"
              fullWidth
              SelectProps={{ native: true }}
              helperText="How often to check for changes"
            >
              <option value="hourly">Every Hour</option>
              <option value="daily">Daily (4 AM UTC)</option>
              <option value="weekly">Weekly</option>
            </TextField>

            <TextField
              label="Deduplication Window (days)"
              type="number"
              value={7}
              fullWidth
              helperText="Suppress duplicate change notifications within this period"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button variant="outlined">Reset to Defaults</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Configuration
        </Button>
      </Stack>
    </Box>
  );
}
